// import classNames from "classnames";
import { capitalize } from "lodash";
import qs from "qs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  useExpanded,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { useSticky } from "react-table-sticky";
import { useLocalStorage } from "react-use";

import { User } from "pwapi";

import {
  CaseListDirection,
  SubQueueFilters,
  QueueEnum,
} from "shared/reducers/casesReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import AdminLink from "components/AdminLink";
import ApplicationState from "store/ApplicationState";

import CaseColumns from "./CaseColumns";
import CaseNavigationTabsController from "./caseNavigationTabs/CaseNavigationTabsController";
import CaseFilterForm from "./casesFilters/CaseFilterForm";
import CasesTableView from "./CasesTableView";
import ListLayout from "../ListLayout";
import ListPagination from "../ListPagination";

type CaseListProps = {
  columns: any;
  features?: FeatureFlags;
  queue?: QueueEnum;
  includeSignedOut: boolean;
  status: "active" | "working" | "flagged" | "finalized" | null;
};

export const DEFAULT_CASE_LIST_SORT_COLUMN = "priority";
export const DEFAULT_FINALIZED_CASE_LIST_SORT_COLUMN = "finalized_at";

export const getDefaultCaseListSortDirection = (queue?: QueueEnum) =>
  queue === QueueEnum.Working || queue === QueueEnum.Active
    ? CaseListDirection.Asc
    : CaseListDirection.Desc;

const qParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });

const CaseList = ({
  columns,
  features,
  queue,
  includeSignedOut,
  status = null,
}: CaseListProps) => {
  const {
    results: cases = [],
    count: totalRows,
    count_specimen: specimenCount,
    lastCaseViewed,
    loading,
  } = useSelector(({ cases }: ApplicationState) => cases);
  const location = useLocation();

  const user: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );

  const [casePageSize, setCasePageSize] = useLocalStorage<number>(
    "caseListDefaultPageSize"
  );

  const [currentSubQueue, setCurrentSubQueue] = useState<
    keyof typeof SubQueueFilters | undefined
  >(
    (qs.parse(location.search, { ignoreQueryPrefix: true })
      .subQueue as keyof typeof SubQueueFilters) ?? undefined
  );

  const prevQueue = useRef<QueueEnum | undefined>(queue);

  // Only perform this useEffect if the queue is changing via the tabs
  useEffect(() => {
    if (prevQueue.current !== queue) {
      const currQuery = qs.parse(location.search, { ignoreQueryPrefix: true });
      const initSortDirection =
        currQuery.sortDirection || getDefaultCaseListSortDirection(queue);
      if (!currQuery.sortColumn) {
        setSortBy([
          {
            id:
              status === "finalized"
                ? DEFAULT_FINALIZED_CASE_LIST_SORT_COLUMN
                : DEFAULT_CASE_LIST_SORT_COLUMN,
            desc: initSortDirection === CaseListDirection.Desc,
          },
        ]);
      }
      prevQueue.current = queue;
    }
  }, [queue]);

  const {
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,
    setSortBy,

    // Pagination
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    // Selection
    state: { sortBy, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: cases,
      autoResetExpanded: false,
      manualSortBy: true,
      manualPagination: true,
      pageCount: -1,
      disableSortRemove: true,
      initialState: {
        pageIndex:
          qParams.page && parseInt(`${qParams.page}`)
            ? parseInt(`${qParams.page}`) - 1
            : 0,
        pageSize: casePageSize || 10,
        sortBy: [
          {
            id:
              (qParams.sortColumn as string) ||
              (status === "finalized"
                ? DEFAULT_FINALIZED_CASE_LIST_SORT_COLUMN
                : DEFAULT_CASE_LIST_SORT_COLUMN),
            desc: qParams.sortDirection
              ? qParams.sortDirection === CaseListDirection.Desc
              : getDefaultCaseListSortDirection(queue) ===
                CaseListDirection.Desc,
          },
        ],
      },
    },

    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useSticky
  );

  const pageCount = totalRows ? Math.ceil(totalRows / pageSize) : -1;

  const handleChangePageSize = (newValue: string) => {
    setCasePageSize(parseInt(newValue));
  };

  return (
    <ListLayout
      isListLoading={loading}
      resourceCount={cases.length}
      navigation={
        <>
          <h1>
            <AdminLink route={`/admin/cases`} title="QC Admin" />
            &nbsp;
            {queue === QueueEnum.Preliminary
              ? "Cases with Preliminary Report"
              : `${capitalize(queue)} Cases`}
          </h1>
          <CaseNavigationTabsController
            queue={queue || QueueEnum.All}
            setSubQueue={setCurrentSubQueue}
            subQueue={currentSubQueue}
            gotoPage={gotoPage}
          />
        </>
      }
      filters={
        <CaseFilterForm
          queue={queue}
          subQueue={currentSubQueue}
          sortBy={sortBy}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPage={gotoPage}
          setSortBy={setSortBy}
          features={features}
          includeSignedOut={includeSignedOut}
          status={status}
          user={user}
        />
      }
      table={
        <CasesTableView
          headerGroups={headerGroups}
          rows={rows}
          prepareRow={prepareRow}
          lastCaseViewed={lastCaseViewed}
          queue={queue || QueueEnum.All}
        />
      }
      pagination={
        <ListPagination
          canPreviousPage={pageIndex !== 0}
          canNextPage={pageIndex < pageCount - 1}
          gotoPage={gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          pageSizes={[10, 25]}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={pageCount}
          specimenCount={specimenCount}
          totalCount={totalRows}
          toggleAllRowsExpanded={toggleAllRowsExpanded}
          setToLocalStorage={handleChangePageSize}
        />
      }
    />
  );
};

export default CaseColumns(CaseList);
